import type { FileRecord } from "@cohesive/api-client";
import {
  hashKey,
  type QueryClient,
  type QueryKey,
  type Updater,
} from "@tanstack/react-query";
import {
  fileRecordKeys,
  fileRecordListKey,
  fileRecordListScope,
  isFileRecordRowKey,
  isFileRecordScopeListKey,
} from "./fileRecordKeys";

/** A server row, or a field patch for one cached copy of it. */
export type FileRecordPatch = Partial<FileRecord> & Pick<FileRecord, "id">;

/** Fields the list endpoints compute and no mutation response carries. */
const LIST_ONLY_FIELDS = ["tags", "can_edit", "can_manage"] as const;

function mergeRow(prev: FileRecord, patch: FileRecordPatch): FileRecord {
  const next: FileRecord = { ...prev, ...patch };
  for (const field of LIST_ONLY_FIELDS) {
    if (patch[field] === undefined) next[field] = prev[field] as never;
  }
  return next;
}

/**
 * The user's own edit is not a fresher read, so the entry keeps the timestamp
 * it already had. `isRestoredQueryData` judges a restored, links-stripped
 * payload by `dataUpdatedAt`; stamping it with now would pass that payload off
 * as freshly served and hand out rows whose presigned URLs the persister
 * stripped. A live list gains no freshness it did not earn either.
 */
function setKeepingAge<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  updater: Updater<T | undefined, T | undefined>,
): void {
  const state = queryClient.getQueryState<T>(queryKey);
  queryClient.setQueryData<T>(
    queryKey,
    updater,
    state ? { updatedAt: state.dataUpdatedAt } : undefined,
  );
}

/** Every cached list of live rows. The recently-deleted lists hold another population and keep their own writers. */
function cachedLists(queryClient: QueryClient) {
  return queryClient
    .getQueriesData<FileRecord[]>({ queryKey: fileRecordKeys.all })
    .filter(
      (entry): entry is [QueryKey, FileRecord[]] =>
        Array.isArray(entry[1]) && entry[0][1] !== "recentlyDeleted",
    );
}

/**
 * The user's own edit, applied to every cached copy of the row: each list
 * under `fileRecords` that holds it and `["fileRecord", id]` when present. A
 * row whose fields name its scope also joins that scope's cached list and
 * leaves any other, so a move needs no list-specific writes. Fields the
 * patch lacks stay as cached.
 */
export function patchFileRecordCaches(
  queryClient: QueryClient,
  patch: FileRecordPatch,
): void {
  const home = fileRecordListScope(patch);
  const homeHash = home ? hashKey(fileRecordListKey(home)) : null;
  for (const [queryKey, rows] of cachedLists(queryClient)) {
    const index = rows.findIndex((row) => row.id === patch.id);
    const isHome = homeHash !== null && hashKey(queryKey) === homeHash;
    if (index === -1) {
      if (isHome) {
        setKeepingAge<FileRecord[]>(queryClient, queryKey, [
          ...rows,
          patch as FileRecord,
        ]);
      }
      continue;
    }
    if (homeHash !== null && !isHome && isFileRecordScopeListKey(queryKey)) {
      setKeepingAge<FileRecord[]>(
        queryClient,
        queryKey,
        rows.filter((row) => row.id !== patch.id),
      );
      continue;
    }
    const next = rows.slice();
    next[index] = mergeRow(rows[index], patch);
    setKeepingAge<FileRecord[]>(queryClient, queryKey, next);
  }
  const detailKey = fileRecordKeys.detail(patch.id);
  if (queryClient.getQueryData<FileRecord>(detailKey)) {
    setKeepingAge<FileRecord>(queryClient, detailKey, (prev) =>
      prev ? mergeRow(prev, patch) : prev,
    );
  }
}

/**
 * The row leaves every cached list. Its own entry is invalidated rather than
 * removed: a consumer mounted on it keeps the row until the 404 replaces it,
 * and removing the query would strand that observer.
 */
export function removeFileRecordFromCaches(
  queryClient: QueryClient,
  id: string,
): void {
  for (const [queryKey, rows] of cachedLists(queryClient)) {
    if (!rows.some((row) => row.id === id)) continue;
    setKeepingAge<FileRecord[]>(
      queryClient,
      queryKey,
      rows.filter((row) => row.id !== id),
    );
  }
  const detailKey = fileRecordKeys.detail(id);
  if (queryClient.getQueryState(detailKey)) {
    void queryClient.invalidateQueries({ queryKey: detailKey, exact: true });
  }
}

/** The row under its own id, or the reference row that points at it. */
export function findRowInList(
  rows: readonly FileRecord[],
  id: string,
): FileRecord | null {
  return (
    rows.find((row) => row.id === id) ??
    rows.find((row) => row.target_file_record_id === id) ??
    null
  );
}

/** The row under its own id, from any cached list or its own entry. */
export function findCachedFileRecord(
  queryClient: QueryClient,
  id: string,
): FileRecord | undefined {
  for (const [, rows] of cachedLists(queryClient)) {
    const row = rows.find((candidate) => candidate.id === id);
    if (row) return row;
  }
  return queryClient.getQueryData<FileRecord>(fileRecordKeys.detail(id));
}

/**
 * Where a `file_record.*` event lands: the row's own scope list, every cached
 * list holding the row or a reference to it, and the recents when a row
 * appeared or disappeared.
 */
export function fileRecordEventListKeys(
  queryClient: QueryClient,
  record: FileRecordPatch,
  eventType:
    | "file_record.created"
    | "file_record.updated"
    | "file_record.deleted",
): QueryKey[] {
  const keys = new Map<string, QueryKey>();
  const add = (queryKey: QueryKey) => keys.set(hashKey(queryKey), queryKey);
  const home = fileRecordListScope(record);
  if (home) add(fileRecordListKey(home));
  for (const [queryKey, rows] of cachedLists(queryClient)) {
    if (
      rows.some(
        (row) =>
          row.id === record.id || row.target_file_record_id === record.id,
      )
    ) {
      add(queryKey);
    }
  }
  if (eventType !== "file_record.updated") add(fileRecordKeys.recent());
  return Array.from(keys.values());
}

/** The row's own entry and any cached reference entry that renders as it. */
export function invalidateFileRecordRowEntries(
  queryClient: QueryClient,
  id: string,
): void {
  for (const [queryKey, row] of queryClient.getQueriesData<FileRecord>({
    queryKey: [fileRecordKeys.detail("")[0]],
    predicate: (query) => isFileRecordRowKey(query.queryKey),
  })) {
    if (queryKey[1] === id || row?.target_file_record_id === id) {
      void queryClient.invalidateQueries({ queryKey, exact: true });
    }
  }
}

/** Re-read every cached copy of a row: the lists holding it and its own entry. */
export function invalidateCachedFileRecord(
  queryClient: QueryClient,
  id: string,
): void {
  for (const [queryKey, rows] of cachedLists(queryClient)) {
    if (rows.some((row) => row.id === id)) {
      void queryClient.invalidateQueries({ queryKey, exact: true });
    }
  }
  invalidateFileRecordRowEntries(queryClient, id);
}

/** Shortest gap between two re-reads of one list while events keep arriving. */
export const LIST_REREAD_GAP_MS = 2_000;

export type ListNudges = Map<
  string,
  { lastAt: number; trailing?: ReturnType<typeof setTimeout> }
>;

/**
 * Re-reads a list for an event: the first nudge at once, then at most one
 * trailing re-read per gap for everything that arrives meanwhile.
 */
export function nudgeList(
  nudges: ListNudges,
  queryClient: QueryClient,
  queryKey: QueryKey,
): void {
  const hash = hashKey(queryKey);
  const now = Date.now();
  for (const [key, entry] of nudges) {
    if (!entry.trailing && now - entry.lastAt >= LIST_REREAD_GAP_MS) {
      nudges.delete(key);
    }
  }
  const reread = () => {
    nudges.set(hash, { lastAt: Date.now() });
    void queryClient.invalidateQueries({ queryKey });
  };
  const entry = nudges.get(hash);
  if (!entry) {
    reread();
    return;
  }
  if (entry.trailing) return;
  entry.trailing = setTimeout(reread, entry.lastAt + LIST_REREAD_GAP_MS - now);
}
